import { useState, useCallback } from 'react';
import * as filesApi from '../api/files';
import type { FileResponse, ListFilesResponse } from '../types';
import { handleApiError } from '../api/client';
import { calculateRangeProof } from '../utils/file-hash';
import { chooseMultipartPartSizeBytes, DEFAULT_MULTIPART_THRESHOLD_BYTES, uploadMultipartFile } from '../utils/multipartUpload';
import { deleteMultipartResumeSession, getMultipartResumeSession, upsertMultipartResumeSession } from '../utils/multipartResumeStore';

export interface UseFilesState {
  files: FileResponse[];
  totalFiles: number;
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
  error: string | null;
}

export interface UseFilesReturn extends UseFilesState {
  fetchFiles: (params?: filesApi.ListFilesParams) => Promise<void>;
  uploadFile: (
    file: File,
    onProgress?: (progress: number) => void,
    estimatedDurationSeconds?: number,
    originalFileName?: string,
    contentHash?: string,
    onInstantUpload?: () => void,
    signal?: AbortSignal,
    preflightTranscription?: boolean
  ) => Promise<FileResponse>;
  deleteFile: (fileId: string) => Promise<void>;
  moveFile: (fileId: string, folderId: string | null) => Promise<FileResponse>;
  deleteOriginalFile: (fileId: string) => Promise<void>;
  getDownloadURL: (fileId: string) => Promise<string>;
  refreshFiles: () => Promise<void>;
}

export function useFiles(): UseFilesReturn {
  const [state, setState] = useState<UseFilesState>({
    files: [],
    totalFiles: 0,
    totalPages: 0,
    currentPage: 1,
    isLoading: false,
    error: null,
  });

  const [lastParams, setLastParams] = useState<filesApi.ListFilesParams | undefined>();

  const fetchFiles = useCallback(async (params?: filesApi.ListFilesParams) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    setLastParams(params);

    try {
      const response: ListFilesResponse = await filesApi.listFiles(params);
      setState({
        files: response.files,
        totalFiles: response.total,
        totalPages: response.total_pages,
        currentPage: response.page,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: handleApiError(err),
      }));
    }
  }, []);

  const refreshFiles = useCallback(async () => {
    await fetchFiles(lastParams);
  }, [fetchFiles, lastParams]);

  const uploadFile = useCallback(
    async (
      file: File,
      onProgress?: (progress: number) => void,
      estimatedDurationSeconds?: number,
      originalFileName?: string,
      contentHash?: string,
      onInstantUpload?: () => void,
      signal?: AbortSignal,
      preflightTranscription?: boolean
    ): Promise<FileResponse> => {
      const shouldUseMultipart = file.size > DEFAULT_MULTIPART_THRESHOLD_BYTES;
      const preflight = preflightTranscription === true;

      if (shouldUseMultipart) {
        const partSizeBytes = chooseMultipartPartSizeBytes(file.size);

        const normalizedHash = contentHash?.trim().toLowerCase() || '';
        const existing = normalizedHash ? getMultipartResumeSession(normalizedHash) : null;

        // If we have a hash and there's no resumable multipart session, attempt instant upload for duplicates.
        // This avoids reuploading large files when content already exists server-side.
        if (normalizedHash && !existing) {
          try {
            const dup = await filesApi.checkDuplicates({ items: [{ hash: normalizedHash, size: file.size }] });
            const first = dup.results?.[0];
            if (first?.instant_upload_available) {
              const initResponse = await filesApi.initiateUpload({
                file_name: file.name,
                original_file_name: originalFileName,
                file_size: file.size,
                content_type: file.type || 'application/octet-stream',
                estimated_duration_seconds: estimatedDurationSeconds,
                preflight_transcription: preflight,
                content_hash: normalizedHash,
              });

              if (initResponse.requires_proof && initResponse.challenge) {
                try {
                  const proof = await calculateRangeProof(file, initResponse.challenge.offset, initResponse.challenge.length);
                  const claimedFile = await filesApi.claimInstantUpload(initResponse.file_id, {
                    token: initResponse.challenge.token,
                    proof_sha256: proof,
                  });
                  onInstantUpload?.();
                  await refreshFiles();
                  return claimedFile;
                } catch {
                  // If claim fails, clean up and proceed with multipart upload.
                  try {
                    await filesApi.deleteFile(initResponse.file_id);
                  } catch {
                    // ignore
                  }
                }
              } else {
                // Unexpected (duplicate indicated but no proof flow); clean up and proceed with multipart.
                try {
                  await filesApi.deleteFile(initResponse.file_id);
                } catch {
                  // ignore
                }
              }
            }
          } catch {
            // If duplicate check fails, proceed with multipart (server remains source of truth).
          }
        }

        try {
          const confirmedFile = await uploadMultipartFile({
            file,
            partSizeBytes,
            onProgress,
            signal,
            request: {
              file_name: file.name,
              original_file_name: originalFileName,
              file_size: file.size,
              content_type: file.type || 'application/octet-stream',
              estimated_duration_seconds: estimatedDurationSeconds,
              preflight_transcription: preflight,
            },
            resume: existing
              ? {
                  fileId: existing.fileId,
                  uploadId: existing.uploadId,
                  partSize: existing.partSize,
                  partCount: existing.partCount,
                  lastPartSize: existing.lastPartSize,
                  completedParts: existing.completedParts,
                  needsConfirm: existing.needsConfirm,
                }
              : undefined,
            onInitiated: (init) => {
              if (!normalizedHash) return;
              const now = Date.now();
              upsertMultipartResumeSession({
                version: 1,
                contentHash: normalizedHash,
                fileId: init.file_id,
                uploadId: init.upload_id,
                fileName: file.name,
                fileSize: file.size,
                contentType: file.type || 'application/octet-stream',
                partSize: init.part_size,
                partCount: init.part_count,
                lastPartSize: init.last_part_size,
                completedParts: {},
                createdAtMs: now,
                updatedAtMs: now,
              });
            },
            onPartETag: (partNumber, etag) => {
              if (!normalizedHash) return;
              const current = getMultipartResumeSession(normalizedHash);
              if (!current) return;
              upsertMultipartResumeSession({
                ...current,
                completedParts: { ...current.completedParts, [partNumber]: etag },
                updatedAtMs: Date.now(),
              });
            },
            onNeedsConfirm: (fileId) => {
              if (!normalizedHash) return;
              const current = getMultipartResumeSession(normalizedHash);
              if (!current) return;
              upsertMultipartResumeSession({
                ...current,
                fileId,
                needsConfirm: true,
                updatedAtMs: Date.now(),
              });
            },
            onCompleted: () => {
              if (!normalizedHash) return;
              deleteMultipartResumeSession(normalizedHash);
            },
            initiate: filesApi.initiateMultipartUpload,
            getPartUrl: filesApi.getPartUploadURL,
            uploadToPresignedUrl: filesApi.uploadToPresignedURL,
            complete: filesApi.completeMultipartUpload,
            confirm: filesApi.confirmUpload,
            abort: filesApi.abortMultipartUpload,
          });
          await refreshFiles();
          return confirmedFile;
        } catch (err) {
          // If the user canceled, the uploader aborts server-side, so local resume state is invalid.
          if (normalizedHash && err && typeof err === 'object' && 'name' in err && (err as { name?: string }).name === 'AbortError') {
            deleteMultipartResumeSession(normalizedHash);
          } else if (normalizedHash && err instanceof Error && /status\s+(400|401|403|404)\b/.test(err.message)) {
            deleteMultipartResumeSession(normalizedHash);
          }
          throw err;
        }
      }

      const initRequest: filesApi.InitiateUploadRequest = {
        file_name: file.name,
        original_file_name: originalFileName,
        file_size: file.size,
        content_type: file.type || 'application/octet-stream',
        estimated_duration_seconds: estimatedDurationSeconds,
        preflight_transcription: preflight,
      };
      if (contentHash) {
        initRequest.content_hash = contentHash;
      }

      // Step 1: Initiate upload
      const initResponse = await filesApi.initiateUpload(initRequest);

      if (initResponse.requires_proof && initResponse.challenge) {
        try {
          const proof = await calculateRangeProof(file, initResponse.challenge.offset, initResponse.challenge.length);
          const claimedFile = await filesApi.claimInstantUpload(initResponse.file_id, {
            token: initResponse.challenge.token,
            proof_sha256: proof,
          });

          onInstantUpload?.();
          await refreshFiles();
          return claimedFile;
        } catch {
          // Best-effort cleanup of the pending claim file, then fall back to normal upload.
          try {
            await filesApi.deleteFile(initResponse.file_id);
          } catch {
            // ignore
          }
        }
      }

      if (!initResponse.upload_url) {
        // Fall back to normal upload without content hash.
        const fallbackResponse = await filesApi.initiateUpload({
          file_name: file.name,
          original_file_name: originalFileName,
          file_size: file.size,
          content_type: file.type || 'application/octet-stream',
          estimated_duration_seconds: estimatedDurationSeconds,
          preflight_transcription: preflight,
        });
        if (!fallbackResponse.upload_url) {
          throw new Error('Upload URL not returned by server');
        }
        await filesApi.uploadToPresignedURL(fallbackResponse.upload_url, file, onProgress, signal);
        const confirmedFile = await filesApi.confirmUpload(fallbackResponse.file_id);
        await refreshFiles();
        return confirmedFile;
      }

      // Step 2: Upload file to presigned URL
      await filesApi.uploadToPresignedURL(initResponse.upload_url, file, onProgress, signal);

      // Step 3: Confirm upload
      const confirmedFile = await filesApi.confirmUpload(initResponse.file_id);

      // Refresh file list
      await refreshFiles();

      return confirmedFile;
    },
    [refreshFiles]
  );

  const deleteFile = useCallback(
    async (fileId: string) => {
      await filesApi.deleteFile(fileId);
      // Remove from local state
      setState((prev) => ({
        ...prev,
        files: prev.files.filter((f) => f.id !== fileId),
        totalFiles: prev.totalFiles - 1,
      }));
    },
    []
  );

  const getDownloadURL = useCallback(async (fileId: string): Promise<string> => {
    const response = await filesApi.getDownloadURL(fileId);
    return response.download_url;
  }, []);

  const moveFile = useCallback(
    async (fileId: string, folderId: string | null): Promise<FileResponse> => {
      const updatedFile = await filesApi.moveFile(fileId, folderId);
      // Update local state
      setState((prev) => ({
        ...prev,
        files: prev.files.map((f) => (f.id === fileId ? updatedFile : f)),
      }));
      return updatedFile;
    },
    []
  );

  const deleteOriginalFile = useCallback(
    async (fileId: string): Promise<void> => {
      await filesApi.deleteOriginalFile(fileId);
      // Refresh to get updated status
      await refreshFiles();
    },
    [refreshFiles]
  );

  return {
    ...state,
    fetchFiles,
    uploadFile,
    deleteFile,
    moveFile,
    deleteOriginalFile,
    getDownloadURL,
    refreshFiles,
  };
}

export default useFiles;
